import React, { useState, useEffect } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import styled from 'styled-components';
import { navDelay, loaderDelay } from '@utils';
import { usePrefersReducedMotion } from '@hooks';

const StyledHeroSection = styled.section`
  ${({ theme }) => theme.mixins.flexCenter};
  flex-direction: column;
  align-items: flex-start;
  min-height: 100vh;
  height: 100vh;
  padding: 0;

  @media (max-height: 700px) and (min-width: 700px), (max-width: 360px) {
    height: auto;
    padding-top: var(--nav-height);
  }

  h1 {
    margin: 0 0 30px 4px;
    color: var(--light-slate);
    font-family: var(--font-mono);
    font-size: clamp(var(--fz-xs), 4vw, var(--fz-sm));
    font-weight: 500;
    letter-spacing: 0.18em;
    text-transform: uppercase;

    @media (max-width: 480px) {
      margin: 0 0 20px 2px;
    }
  }

  h3 {
    margin-top: 5px;
    color: var(--lightest-slate);
    line-height: 1.1;
  }

  p {
    margin: 20px 0 0;
    max-width: 680px;
  }

  .eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;
    padding: 10px 14px;
    border-radius: 999px;
    border: 1px solid var(--lightest-navy);
    background: rgba(255, 255, 255, 0.03);
    font-family: var(--font-mono);
    font-size: var(--fz-xs);
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--light-slate);
  }

  .gradient-text {
    background: linear-gradient(120deg, var(--green), #ffd7a3);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  .secondary-heading {
    font-size: clamp(28px, 6vw, 42px);
    color: var(--light-slate);
  }
`;

const StyledHighlightList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 28px 0 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  grid-gap: 14px;

  li {
    padding: 16px 18px;
    border-radius: var(--border-radius);
    border: 1px solid var(--lightest-navy);
    background: linear-gradient(145deg, rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.01));
    backdrop-filter: blur(2px);
    color: var(--lightest-slate);
    box-shadow: 0 8px 28px -18px var(--navy-shadow);

    small {
      display: block;
      margin-bottom: 6px;
      font-family: var(--font-mono);
      font-size: var(--fz-xxs);
      letter-spacing: 0.18em;
      text-transform: uppercase;
      color: var(--light-slate);
    }
  }
`;

const StyledCTAGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  align-items: center;
  margin-top: 36px;

  .email-link {
    ${({ theme }) => theme.mixins.bigButton};
    text-transform: none;
  }

  .ghost-button {
    ${({ theme }) => theme.mixins.smallButton};
    background: transparent;
    color: var(--lightest-slate);
    border: 1px solid var(--light-slate);
    box-shadow: none;
    text-transform: none;

    &:hover,
    &:focus-visible {
      background: rgba(255, 255, 255, 0.05);
      transform: translateY(-2px);
      box-shadow: 0 10px 22px -18px rgba(0, 0, 0, 0.4);
    }
  }
`;

const Hero = () => {
  const [isMounted, setIsMounted] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    const timeout = setTimeout(() => setIsMounted(true), navDelay);
    return () => clearTimeout(timeout);
  }, []);

  const one = <h1>Hi, my name is</h1>;
  const two = <h2 className="big-heading">Mohammed Altarhouni.</h2>;
  const three = <h3 className="big-heading">I build secure digital products.</h3>;
  const four = (
    <>
      <p>
        I’m a software engineer who designs and hardens digital products for people who rely on
        them every day. My work blends backend engineering, mobile experiences, and hands-on
        operations so teams can move fast without sacrificing safety. I’m currently expanding my
        research around AI-powered observability while pursuing a master’s degree in computer
        engineering at{' '}
        <a href="https://pwr.edu.pl/en/" target="_blank" rel="noreferrer">
          Wrocław University of Science and Technology
        </a>
        .
      </p>
    </>
  );

  const heroHighlights = [
    {
      label: 'Banking-scale delivery',
      detail: 'Mobile & web releases that stay reliable for thousands of concurrent users.',
    },
    {
      label: 'Security-first tooling',
      detail: 'OWASP-aligned reviews, encrypted data flows, and on-call playbooks that actually work.',
    },
    {
      label: 'Developer enablement',
      detail: 'CI/CD pipelines, API standards, and documentation that help teams move together.',
    },
  ];

  const five = (
    <StyledHighlightList>
      {heroHighlights.map(({ label, detail }) => (
        <li key={label}>
          <small>{label}</small>
          {detail}
        </li>
      ))}
    </StyledHighlightList>
  );

  const six = (
    <StyledCTAGroup>
      <a className="email-link" href="mailto:altarhonymohammed@gmail.com">
        Start a project
      </a>
      <a
        className="ghost-button"
        href="https://github.com/QassimAltarhouni"
        target="_blank"
        rel="noreferrer"
      >
        Browse my build logs
      </a>
    </StyledCTAGroup>
  );

  const items = [one, two, three, four, ];

  return (
    <StyledHeroSection>
      {prefersReducedMotion ? (
        <>
          {items.map((item, i) => (
            <div key={i}>{item}</div>
          ))}
        </>
      ) : (
        <TransitionGroup component={null}>
          {isMounted &&
            items.map((item, i) => (
              <CSSTransition key={i} classNames="fadeup" timeout={loaderDelay}>
                <div style={{ transitionDelay: `${i + 1}00ms` }}>{item}</div>
              </CSSTransition>
            ))}
        </TransitionGroup>
      )}
    </StyledHeroSection>
  );
};

export default Hero;
